import { getConnectorConfigPartsSchema, parseConnectorPlainConfigDraft } from '@main/integrations';
import {
  getObjectShape,
  getSchemaFieldPaths,
  isOptionalSchema,
  setPath,
  unwrapTransparentSchema,
} from '@main/shared/utils';
import { Namespace, TFunction } from 'i18next';
import z from 'zod/v4';

import { nullToUndefined, pickDirtyValues } from '../schema-form/form-value-utils';
import { FieldSlotDescriptor, FormModel } from '../schema-form/model';
import { createSchemaFieldModel } from '../schema-form/schema-field-model';

export type ConnectorConfigDraftValues = {
  config: Record<string, unknown>;
  encryptedConfig: Record<string, unknown>;
};

export type ConnectorConfigSubmit = {
  config: Record<string, unknown>;
  encryptedConfigPatch: Record<string, unknown>;
};

export type EncryptedConfigFieldState = {
  path: string[];
  configured: boolean;
};

export type ConnectorFieldMeta =
  | { part: 'plainConfig' }
  | {
      part: 'encryptedConfig';
      writeOnly: true;
      configured: boolean;
      clearable: boolean;
      clearState: 'unchanged' | 'cleared';
    };

export type ConnectorConfigSlot = FieldSlotDescriptor & {
  kind: 'connector.encryptedFieldFooter';
};

export type ConnectorConfigFormModel = FormModel<
  ConnectorConfigDraftValues,
  ConnectorFieldMeta,
  ConnectorConfigSubmit,
  ConnectorConfigSlot
>;

export type CreateConnectorConfigFormModelInput = {
  configSchema: z.ZodObject;
  encryptedConfigSchema: z.ZodObject;
  defaultConfig?: Record<string, unknown>;
  encryptedFields: readonly EncryptedConfigFieldState[];
  clearedEncryptedPaths: ReadonlySet<string>;
  t: TFunction<Namespace>;
  tKey: string;
};

export function createConnectorConfigFormModel(
  input: CreateConnectorConfigFormModelInput,
): ConnectorConfigFormModel {
  const configuredEncryptedPaths = new Set(
    input.encryptedFields.filter((field) => field.configured).map((field) => field.path.join('.')),
  );
  const encryptedSchemaFieldPaths = new Set(
    getSchemaFieldPaths(input.encryptedConfigSchema).map((path) => path.join('.')),
  );
  const encryptedFieldStateByPath = new Map(
    input.encryptedFields.map((field) => [field.path.join('.'), field]),
  );
  const encryptedValidationSchema = makeConfiguredFieldsOptional(
    input.encryptedConfigSchema,
    [],
    configuredEncryptedPaths,
  );
  const enterValuePlaceholder = input.t('configureForm.placeholders.enterValue');
  const valueSetPlaceholder = input.t('configureForm.placeholders.valueSet');
  const valueClearedPlaceholder = input.t('configureForm.placeholders.valueCleared');

  const plainFields = createSchemaFieldModel<
    ConnectorConfigDraftValues,
    ConnectorFieldMeta,
    ConnectorConfigSlot
  >({
    schema: input.configSchema,
    namePrefix: 'config',
    pathPrefix: [],
    t: input.t,
    tKey: `${input.tKey}.plain`,
    placeholder: enterValuePlaceholder,
    fieldPolicy: () => ({
      meta: { part: 'plainConfig' },
    }),
  });
  const encryptedFields = createSchemaFieldModel<
    ConnectorConfigDraftValues,
    ConnectorFieldMeta,
    ConnectorConfigSlot
  >({
    schema: input.encryptedConfigSchema,
    namePrefix: 'encryptedConfig',
    pathPrefix: [],
    t: input.t,
    tKey: `${input.tKey}.encrypted`,
    fieldPolicy: ({ path, schema, required }) => {
      const key = path.join('.');
      const encryptedFieldState = encryptedFieldStateByPath.get(key);
      const configured = encryptedFieldState?.configured ?? false;
      const cleared = input.clearedEncryptedPaths.has(key);
      const clearable = configured && isOptionalSchema(schema);
      const isEncryptedField = encryptedSchemaFieldPaths.has(key);

      return {
        required: {
          contract: required.contract,
          input: configured && !cleared ? false : required.input,
        },
        input: {
          placeholder: cleared
            ? valueClearedPlaceholder
            : configured
              ? valueSetPlaceholder
              : enterValuePlaceholder,
          disabled: cleared,
          isSecret: isEncryptedField,
        },
        slots: clearable
          ? { footer: { kind: 'connector.encryptedFieldFooter' as const } }
          : undefined,
        meta: {
          part: 'encryptedConfig',
          writeOnly: true,
          configured,
          clearable,
          clearState: cleared ? 'cleared' : 'unchanged',
        },
      };
    },
  });

  return {
    inputSchema: getConnectorConfigPartsSchema({
      configSchema: input.configSchema,
      encryptedConfigSchema: encryptedValidationSchema,
    }),
    defaultValues: {
      config:
        input.defaultConfig === undefined
          ? {}
          : parseConnectorPlainConfigDraft(
              input.configSchema,
              nullToUndefined(input.defaultConfig),
            ),
      encryptedConfig: {},
    },
    fields: [...plainFields, ...encryptedFields],
    serialize: ({ values, dirtyFields }) => {
      const encryptedConfigPatch = pickDirtyValues(
        values.encryptedConfig,
        dirtyFields.encryptedConfig,
      );

      for (const key of input.clearedEncryptedPaths) {
        const path = key.split('.');
        setPath(encryptedConfigPatch, path, null);
      }

      return {
        config: values.config,
        encryptedConfigPatch,
      };
    },
  };
}

function makeConfiguredFieldsOptional(
  schema: z.ZodObject,
  parentPath: readonly string[],
  configured: ReadonlySet<string>,
): z.ZodObject {
  return z.object(
    Object.fromEntries(
      Object.entries(getObjectShape(schema)).map(([key, fieldSchema]) => {
        const currentPath = [...parentPath, key];
        const keyPath = currentPath.join('.');
        const unwrapped = unwrapTransparentSchema(fieldSchema);

        if (unwrapped instanceof z.ZodObject) {
          const nested = makeConfiguredFieldsOptional(unwrapped, currentPath, configured);
          const canOmitObject =
            isOptionalSchema(fieldSchema) ||
            configured.has(keyPath) ||
            hasOnlyOptionalFields(nested);
          return [key, canOmitObject ? nested.optional() : nested];
        }

        return [key, configured.has(keyPath) ? fieldSchema.optional() : fieldSchema];
      }),
    ),
  );
}

function hasOnlyOptionalFields(schema: z.ZodObject) {
  return Object.values(getObjectShape(schema)).every((fieldSchema) =>
    isOptionalSchema(fieldSchema),
  );
}
